import { AfterViewInit, Component, Input } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { GlobalFilterService } from '../../shared-services/global-filter-service';

import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { debounceTime, forkJoin, of, Subject, switchMap, tap } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CustomPayloadService } from '../../../../../my-app/src/app/shared/dashboard-payload-services/custom-payload.service';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-table',
  imports: [
    MatTableModule,
    CommonModule,
    MatProgressSpinnerModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
  ],
  standalone: true,
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent implements AfterViewInit {
  @Input() data!: any;
  private readonly discountTypeKey = 'discount_type';
  private readonly lineDiscountKey = 'line_discount';
  private readonly compactColumnKeys = new Set<string>([
    'quote_level_discount'
  ]);
  private readonly fixedDiscountColumnKeys = new Set<string>([
    'discount_type',
    'line_discount'
  ]);
  private readonly fixedColumnWidthMap: Record<string, number> = {
    item_id: 220,
    discount_type: 260,
    line_discount: 260
  };
  private readonly wideColumnKeys = new Set<string>([
    'description',
    'condition'
  ]);
  private readonly numericColumnHints = [
    'line_discount',
    'quote_level_discount',
    'discounted_price',
    'unit_price',
    'price',
    'amount',
    'sales',
    'impact',
    'total',
    'qty',
    'quantity',
    'rate',
    'percent',
    'percentage',
    'usd'
  ];
  private readonly nonNumericColumnHints = [
    'discount_type',
    'type',
    'reason',
    'description',
    'name',
    'clause',
    'condition',
    'bundle'
  ];
  displayedColumns: string[] = [];
  filter: any;
  isLoading = true;
  columnLabels: { [key: string]: string } = {};
  columnConfigMap: { [key: string]: any } = {};
  dataSource: any[] = [];
  private unSubscribe$: Subject<void> = new Subject();
  @Input() set widgetId(val: string) {
    this._widgetId = val;
  }
  get widgetId(): string {
    return this._widgetId || this.data?.widgetId;
  }
  private _widgetId!: string;
  pagedData: any[] = [];
  private originalDataSource: any[] = [];
  isActionLoading = false;
  private restoredFromWidgetData = false;

  private pageSize: number = 11;
  private pageIndex: number = 0;

  constructor(
    private configService: CustomPayloadService,
    private globalFilterService: GlobalFilterService,
    private http: HttpClient
  ) { }

  get tableActions(): any[] {
    return Array.isArray(this.data?.actions) ? this.data.actions : [];
  }

  get areActionsOutsideTable(): boolean {
    return this.data?.actionsOutsideTable === true;
  }

  ngAfterViewInit(): void {
    this.initializeColumns();
    const isCaseSpecificApiCall = this.isCaseSpecificApiCall();
    const shouldAutoLoadApi = this.shouldAutoLoadApi();
    this.restoredFromWidgetData = !isCaseSpecificApiCall && this.restoreFromWidgetData();

    if (!this.restoredFromWidgetData && Array.isArray(this.data?.tableData)) {
      const seededRows = this.data.tableData.map((row: any) => ({ ...row }));
      this.dataSource = this.normalizeRowsToConfiguredColumns(seededRows);
      this.originalDataSource = [...this.dataSource];
      this.refreshDisplayedColumnsFromData();
      this.initializeDropdownDefaults(this.dataSource);
      this.publishWidgetData();
      if (!!this.data?.isPagination) {
        this.setPagedData();
      }
      this.isLoading = false;
    }

    // Subscribe to table data updates
    if (this.data?.useGlobalTableData !== false) {
      this.globalFilterService.tableData$.subscribe((data: any[]) => {
        if (data) {
          const existingWidgetRows = this.globalFilterService.getWidgetData(this.widgetId);
          const shouldIgnoreEmptyEmission =
            data.length === 0 &&
            this.dataSource.length > 0 &&
            existingWidgetRows.length > 0;

          if (shouldIgnoreEmptyEmission) {
            return;
          }

          if (data.length > 0) {
            const incomingRows = this.normalizeRowsToConfiguredColumns([...data]);
            this.dataSource = this.shouldMergeIncomingGlobalTableData()
              ? this.mergeIncomingRowsWithCurrentData(incomingRows)
              : incomingRows;
            this.dataSource = this.normalizeRowsToConfiguredColumns(this.dataSource);
            this.initializeDropdownDefaults(this.dataSource);
            this.refreshDisplayedColumnsFromData();
            this.publishWidgetData();
          } else {
            this.dataSource = [];
            this.displayedColumns = this.data?.headerColumns?.map((col: any) => col.key) || [];
            this.publishWidgetData();
          }
          this.originalDataSource = [...this.dataSource];
          if (!!this.data?.isPagination) {
            this.setPagedData();
          }
        }
      });
    }

    this.globalFilterService.resetState$
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(() => {
        this.resetTableState();
      });

    if (this.data?.apiUrl && shouldAutoLoadApi) {
      if (isCaseSpecificApiCall) {
        this.isLoading = true;
        this.http.post(this.data.apiUrl, this.getCaseSpecificPayload()).subscribe({
          next: (res: any) => {
            this.applyTableResponse(res);
          },
          error: (err: any) => {
            this.isLoading = false;
            console.error('Error loading table data', err);
          }
        });
        return;
      }

      this.globalFilterService.filter$
        .pipe(takeUntil(this.unSubscribe$),
          debounceTime(0),
          tap(() => {
            this.isLoading = true;
          }),
          switchMap((filters) => this.loadAndRenderChart(filters))
        )
        .subscribe({
          next: (res: any) => {
            this.applyTableResponse(res);
          },
          error: (err) => {
            this.isLoading = false;
            console.error('Error loading table data', err);
          },
        });
    } else {
      this.isLoading = false;
    }
  }




  // ngAfterViewInit(): void {
  //   // Set columns early
  //   this.displayedColumns =
  //     this.data?.headerColumns?.map((col: any) => col.key) || [];
  //   this.columnLabels = Object.fromEntries(
  //     this.data?.headerColumns?.map((col: any) => [col.key, col.label]) || []
  //   );

  //   // Subscribe to table data updates
  //   this.globalFilterService.tableData$.subscribe((data: any[]) => {
  //     if (data) {
  //       console.log('Table data received:', data);
  //       if (data.length > 0) {
  //         console.log('Table data received 2:', data);
  //         const newData = cloneDeep(data); // Use deep clone here
  //         this.dataSource = cloneDeep(newData); // Deep copy of newData to avoid mutations

  //         // Filter columns based on data availability
  //         const allKeys = new Set<string>();
  //         this.dataSource.forEach(row => {
  //           Object.keys(row).forEach(key => {
  //             if (row[key] !== null && row[key] !== undefined && row[key] !== '') {
  //               allKeys.add(key);
  //             }
  //           });
  //         });

  //         const configuredColumns = this.data?.headerColumns?.map((col: any) => col.key) || [];
  //         console.log('Table data received 3:', configuredColumns);
  //         this.displayedColumns = configuredColumns.filter((key: string) => allKeys.has(key));
  //         console.log('Table data received 4:', this.displayedColumns);

  //       } else {
  //         // Handle empty data (for cancel/reset functionality)
  //         this.dataSource = [];
  //         // Reset check to show all columns or none? explicit request was "only show those columns which are from that response"
  //         // If empty, maybe show all headers as default
  //         this.displayedColumns = this.data?.headerColumns?.map((col: any) => col.key) || [];
  //         console.log('Table data received 5:', this.displayedColumns);
  //       }
  //       this.originalDataSource = cloneDeep(this.dataSource); // Deep copy of dataSource to avoid mutations
  //       console.log('Table data received 6:', this.originalDataSource);
  //       if (!!this.data?.isPagination) {
  //         this.setPagedData();
  //       }
  //     }
  //   });

  //   this.globalFilterService.filter$
  //     .pipe(takeUntil(this.unSubscribe$),
  //       debounceTime(2000), // added debounce
  //       tap(() => {
  //         this.isLoading = true; // Start loader
  //       }),
  //       switchMap((filters) => this.loadAndRenderChart(filters))
  //     )
  //     .subscribe({
  //       next: (res: any) => {
  //         let tableData = res?.[this.data?.responseKey || 'responseList'] || [];
  //         this.isLoading = false;

  //         this.originalDataSource = cloneDeep(tableData); // Deep copy of tableData
  //         this.dataSource = cloneDeep(this.originalDataSource); // Deep copy of originalDataSource
  //         if (!!this.data?.isPagination) {
  //           this.setPagedData();
  //         }
  //       },
  //       error: (err) => {
  //         this.isLoading = false;
  //         console.error('Error loading table data', err);
  //       },
  //     });
  // }

  private loadAndRenderChart(filters?: any): any {
    if (!this.data?.apiUrl) {
      return of({});
    }
    return this.configService
      .getConfig(this.data.apiUrl, filters, 'post', true)
      .pipe(takeUntil(this.unSubscribe$));
  }

  private isCaseSpecificApiCall(): boolean {
    return this.data?.caseSpecificApiCall === true;
  }

  private shouldAutoLoadApi(): boolean {
    return this.data?.enableTableApiCall === true;
  }

  private getCaseSpecificPayload(): any {
    return this.deepClone(this.data?.caseSpecificPayload || {});
  }

  private applyTableResponse(response: any): void {
    const tableData = this.extractTableRows(response);
    this.isLoading = false;

    this.originalDataSource = this.normalizeRowsToConfiguredColumns(tableData);
    this.dataSource = [...this.originalDataSource];
    this.refreshDisplayedColumnsFromData();
    this.initializeDropdownDefaults(this.dataSource);
    this.publishWidgetData();
    if (!!this.data?.isPagination) {
      this.setPagedData();
    }
  }

  private extractTableRows(response: any): any[] {
    const configuredResponseKey = String(this.data?.responseKey || '').trim();

    if (configuredResponseKey.length > 0) {
      const keyedResponse = response?.[configuredResponseKey];
      if (Array.isArray(keyedResponse)) {
        return keyedResponse;
      }

      if (keyedResponse && typeof keyedResponse === 'object') {
        if (Array.isArray((keyedResponse as any).data)) {
          return (keyedResponse as any).data;
        }

        if (Array.isArray((keyedResponse as any).responseList)) {
          return (keyedResponse as any).responseList;
        }
      }
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.responseList)) {
      return response.responseList;
    }

    return [];
  }

  private initializeColumns(): void {
    const headerColumns = Array.isArray(this.data?.headerColumns) ? this.data.headerColumns : [];
    this.displayedColumns = this.getConfiguredColumnKeys();
    this.columnLabels = Object.fromEntries(
      headerColumns.map((col: any) => [col.key, col.label]) || []
    );
    this.columnConfigMap = Object.fromEntries(
      headerColumns.map((col: any) => [col.key, col]) || []
    );
  }

  isDropdownColumn(column: string): boolean {
    const colConfig = this.columnConfigMap[column];
    return colConfig?.type === 'dropdown' || colConfig?.isDropdown === true;
  }

  getDropdownOptions(column: string): string[] {
    const colConfig = this.columnConfigMap[column];
    const options = colConfig?.options || colConfig?.optionsList || [];
    return Array.isArray(options) ? options : [];
  }

  getDropdownPlaceholder(column: string): string {
    const colConfig = this.columnConfigMap[column];
    const configuredPlaceholder = String(colConfig?.placeholder || '').trim();
    if (configuredPlaceholder) {
      return configuredPlaceholder;
    }

    const label = this.formatHeaderLabel(colConfig?.label || this.columnLabels[column] || column);
    return label ? `Select ${label}` : 'Select';
  }

  formatHeaderLabel(value: any): string {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return '';
    }

    const normalized = raw
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();

    return normalized
      .split(' ')
      .map((word) => {
        if (!word) {
          return word;
        }
        const leading = (word.match(/^[^A-Za-z0-9]*/) || [''])[0];
        const trailing = (word.match(/[^A-Za-z0-9]*$/) || [''])[0];
        const core = word.slice(leading.length, word.length - trailing.length);
        if (!core) {
          return word;
        }

        if (/^[A-Z0-9]{1,3}$/.test(core)) {
          return `${leading}${core}${trailing}`;
        }

        const lowerCore = core.toLowerCase();
        const titleCore = lowerCore.charAt(0).toUpperCase() + lowerCore.slice(1);
        return `${leading}${titleCore}${trailing}`;
      })
      .join(' ');
  }

  isCompactColumn(column: string): boolean {
    return this.compactColumnKeys.has(String(column || '').trim().toLowerCase());
  }

  isFixedDiscountColumn(column: string): boolean {
    return this.fixedDiscountColumnKeys.has(String(column || '').trim().toLowerCase());
  }

  getFixedColumnWidth(column: string): number | null {
    const normalizedColumn = String(column || '').trim().toLowerCase();
    return this.fixedColumnWidthMap[normalizedColumn] ?? null;
  }

  isWideColumn(column: string): boolean {
    return this.wideColumnKeys.has(String(column || '').trim().toLowerCase());
  }

  isNumericColumn(column: string, row?: any): boolean {
    if (!column) {
      return false;
    }

    if (this.isLikelyNumericColumn(column)) {
      return true;
    }

    return this.isNumericValue(row?.[column]);
  }

  getHeaderClasses(column: string): Record<string, boolean> {
    return {
      'compact-column': this.isCompactColumn(column),
      'fixed-discount-column': this.isFixedDiscountColumn(column),
      'wide-column': this.isWideColumn(column),
      'numeric-column': this.isNumericColumn(column)
    };
  }

  getCellClasses(column: string, row: any): Record<string, boolean> {
    return {
      'compact-column': this.isCompactColumn(column),
      'fixed-discount-column': this.isFixedDiscountColumn(column),
      'wide-column': this.isWideColumn(column),
      'numeric-column': this.isNumericColumn(column, row),
      'dropdown-column': this.isDropdownColumn(column)
    };
  }

  onDropdownValueChange(row: any, column: string, value: string): void {
    if (!row) {
      return;
    }

    if (column === this.discountTypeKey && typeof value === 'string' && value.toLowerCase() === 'regional') {
      const lineDiscountOptions = this.getDropdownOptions(this.lineDiscountKey);
      if (lineDiscountOptions.length) {
        const randomIndex = Math.floor(Math.random() * lineDiscountOptions.length);
        row[this.lineDiscountKey] = lineDiscountOptions[randomIndex];
      }
    }

    this.publishWidgetData();
    if (!!this.data?.isPagination) {
      this.setPagedData();
    }
  }

  onTableActionClick(action: any): void {
    if (!action || this.isActionLoading) {
      return;
    }

    const apiUrl = action?.apiUrl;
    if (!apiUrl) {
      console.error('[Table] action apiUrl is missing:', action);
      return;
    }

    const targetRows = this.resolveActionRows(action);
    if (!targetRows.length) {
      console.warn('[Table] No table rows available for action:', action?.key || action?.label);
      return;
    }

    const method = String(action?.method || 'POST').toUpperCase();
    if (method !== 'POST') {
      console.error('[Table] only POST method is supported for table actions right now.');
      return;
    }

    const isBulkRequest = action?.bulkRequest === true || !!action?.requestItemsKey;
    if (isBulkRequest) {
      const payload = this.buildBulkActionPayload(action, targetRows);
      this.isActionLoading = true;
      this.http.post(apiUrl, payload).subscribe({
        next: (response) => {
          console.log('[Table] bulk action response:', response);
          this.applyBulkActionResponse(action, targetRows, response);
          this.isActionLoading = false;
        },
        error: (error) => {
          console.error('[Table] bulk action failed:', error);
          this.isActionLoading = false;
        }
      });
      return;
    }

    const actionRequests = targetRows.map((row) => {
      const payload = this.buildActionPayload(action, row);
      return {
        row,
        request$: this.http.post(apiUrl, payload)
      };
    });

    this.isActionLoading = true;

    if (actionRequests.length === 1) {
      actionRequests[0].request$.subscribe({
        next: (response) => {
          console.log('[Table] action response:', response);
          this.applyActionResponses(action, [{ row: actionRequests[0].row, response }]);
          this.isActionLoading = false;
        },
        error: (error) => {
          console.error('[Table] action failed:', error);
          this.isActionLoading = false;
        }
      });
      return;
    }

    forkJoin(actionRequests.map((request) => request.request$)).subscribe({
      next: (responses) => {
        console.log('[Table] action responses:', responses);
        const rowResponses = responses.map((response, index) => ({
          row: actionRequests[index].row,
          response
        }));
        this.applyActionResponses(action, rowResponses);
        this.isActionLoading = false;
      },
      error: (error) => {
        console.error('[Table] action failed:', error);
        this.isActionLoading = false;
      }
    });
  }

  private resolveActionRows(action: any): any[] {
    const sourceRows = Array.isArray(this.dataSource) ? this.dataSource : [];
    const rowMode = String(action?.rowMode || 'first').toLowerCase();

    if (rowMode === 'all') {
      return sourceRows;
    }

    const firstRow = sourceRows[0];
    return firstRow ? [firstRow] : [];
  }

  private buildActionPayload(action: any, row: any): any {
    const payloadTemplate = this.deepClone(action?.payloadTemplate || action?.payload || {});
    const requestData = this.buildRequestData(action, row);

    const payload = {
      ...payloadTemplate,
      requestData: {
        ...(payloadTemplate?.requestData || {}),
        ...(action?.requestDataStatic || {}),
        ...requestData,
      }
    };

    const topLevelKeys = [
      'responseList',
      'entity',
      'workflowId',
      'endpoints',
      'communicationType',
      'version'
    ];

    topLevelKeys.forEach((key) => {
      if (action?.[key] !== undefined) {
        payload[key] = this.deepClone(action[key]);
      } else if (payload[key] === undefined && key === 'responseList') {
        payload[key] = {};
      }
    });

    return payload;
  }

  private buildBulkActionPayload(action: any, rows: any[]): any {
    const payloadTemplate = this.deepClone(action?.payloadTemplate || action?.payload || {});
    const itemsKey = String(action?.requestItemsKey || 'items').trim() || 'items';
    const items = this.buildBulkActionItems(action, rows);

    const payload = {
      ...payloadTemplate,
      requestData: {
        ...(payloadTemplate?.requestData || {}),
        ...(action?.requestDataStatic || {}),
        [itemsKey]: items
      }
    };

    const topLevelKeys = [
      'responseList',
      'entity',
      'batchId',
      'workflowId',
      'endpoints',
      'communicationType',
      'version'
    ];

    topLevelKeys.forEach((key) => {
      if (action?.[key] !== undefined) {
        payload[key] = this.deepClone(action[key]);
      } else if (payload[key] === undefined && key === 'responseList') {
        payload[key] = {};
      }
    });

    return payload;
  }

  private buildBulkActionItems(action: any, rows: any[]): any[] {
    const rowItems = rows.map((row) => this.buildRequestData(action, row));
    const baseWidgetId = String(action?.baseWidgetId || '').trim();
    if (!baseWidgetId) {
      return rowItems;
    }

    const baseWidgetRows = this.globalFilterService.getWidgetData(baseWidgetId);
    const firstBaseRow = Array.isArray(baseWidgetRows) ? baseWidgetRows[0] : null;
    if (!firstBaseRow || typeof firstBaseRow !== 'object') {
      return rowItems;
    }

    const configuredBaseFields = Array.isArray(action?.baseWidgetFields)
      ? action.baseWidgetFields
      : [];
    const baseFields = configuredBaseFields
      .map((field: any) => String(field || '').trim())
      .filter((field: string) => field.length > 0);
    const fieldsToUse = baseFields.length > 0 ? baseFields : Object.keys(firstBaseRow);

    const baseData = fieldsToUse.reduce((acc: Record<string, any>, field: string) => {
      acc[field] = this.normalizeActionFieldValue(field, firstBaseRow[field]);
      return acc;
    }, {});

    return rowItems.map((item) => ({
      ...baseData,
      ...item
    }));
  }

  private applyBulkActionResponse(action: any, targetRows: any[], response: any): void {
    const responseRows = this.extractResponseRows(response, action);
    if (!responseRows.length) {
      return;
    }

    targetRows.forEach((targetRow, index) => {
      const matchedResponseRow =
        this.findMatchingResponseRowForTarget(action, targetRow, responseRows) ||
        responseRows[index] ||
        null;

      if (!matchedResponseRow) {
        return;
      }

      this.mergeResponseRowIntoTableRow(targetRow, matchedResponseRow);
    });

    this.dataSource = this.normalizeRowsToConfiguredColumns(this.dataSource);
    this.originalDataSource = [...this.dataSource];
    this.refreshDisplayedColumnsFromData();
    this.publishWidgetData();
    if (!!this.data?.isPagination) {
      this.setPagedData();
    }
  }

  private applyActionResponses(
    action: any,
    rowResponses: Array<{ row: any; response: any }>
  ): void {
    rowResponses.forEach(({ row, response }) => {
      const responseRows = this.extractResponseRows(response, action);
      if (!responseRows.length) {
        return;
      }

      const matchedResponseRow =
        this.findMatchingResponseRowForTarget(action, row, responseRows) ||
        responseRows[0];

      if (!matchedResponseRow) {
        return;
      }

      this.mergeResponseRowIntoTableRow(row, matchedResponseRow);
    });

    this.dataSource = this.normalizeRowsToConfiguredColumns(this.dataSource);
    this.originalDataSource = [...this.dataSource];
    this.refreshDisplayedColumnsFromData();
    this.publishWidgetData();
    if (!!this.data?.isPagination) {
      this.setPagedData();
    }
  }

  private refreshDisplayedColumnsFromData(): void {
    const configuredColumns = this.getConfiguredColumnKeys();
    if (configuredColumns.length > 0) {
      this.displayedColumns = configuredColumns;
      return;
    }

    if (!this.dataSource.length) {
      this.displayedColumns = [];
      return;
    }

    const allKeys = new Set<string>();
    this.dataSource.forEach((row) => {
      Object.keys(row || {}).forEach((key) => {
        allKeys.add(key);
      });
    });

    this.displayedColumns = Array.from(allKeys);
  }

  private getConfiguredColumnKeys(): string[] {
    const headerColumns = Array.isArray(this.data?.headerColumns) ? this.data.headerColumns : [];
    return headerColumns
      .map((column: any) => String(column?.key || '').trim())
      .filter((key: string) => key.length > 0);
  }

  private normalizeRowsToConfiguredColumns(rows: any[]): any[] {
    const configuredColumns = this.getConfiguredColumnKeys();
    const safeRows = Array.isArray(rows) ? rows : [];

    if (!configuredColumns.length) {
      return safeRows.map((row: any) => (row && typeof row === 'object' ? { ...row } : {}));
    }

    return safeRows.map((row: any) => {
      const source = row && typeof row === 'object' ? { ...row } : {};
      configuredColumns.forEach((key: string) => {
        if (this.isValueEmpty(source[key])) {
          source[key] = this.isDropdownColumn(key) ? null : '';
        }
      });
      return source;
    });
  }

  private shouldMergeIncomingGlobalTableData(): boolean {
    return this.data?.mergeOnGlobalTableData === true;
  }

  private shouldPreferExistingOnMerge(): boolean {
    return this.data?.preferExistingOnMerge === true;
  }

  private getMergeByField(): string {
    const configuredField = String(this.data?.mergeByField || '').trim();
    if (configuredField) {
      return configuredField;
    }

    const configuredColumns = this.getConfiguredColumnKeys();
    if (configuredColumns.includes('item_id')) {
      return 'item_id';
    }

    return '';
  }

  private mergeIncomingRowsWithCurrentData(incomingRows: any[]): any[] {
    const existingRows = Array.isArray(this.dataSource)
      ? this.dataSource.map((row: any) => ({ ...row }))
      : [];
    const normalizedIncomingRows = Array.isArray(incomingRows)
      ? incomingRows.map((row: any) => ({ ...row }))
      : [];

    if (!existingRows.length) {
      return normalizedIncomingRows;
    }

    if (!normalizedIncomingRows.length) {
      return existingRows;
    }

    const mergeByField = this.getMergeByField();
    if (!mergeByField) {
      return this.dedupeRowsBySignature([...existingRows, ...normalizedIncomingRows]);
    }

    const mergedRows = [...existingRows];
    normalizedIncomingRows.forEach((incomingRow) => {
      const existingIndex = mergedRows.findIndex((existingRow) =>
        this.isSameRowByField(existingRow, incomingRow, mergeByField)
      );

      if (existingIndex < 0) {
        mergedRows.push({ ...incomingRow });
        return;
      }

      mergedRows[existingIndex] = this.mergeMatchedRows(mergedRows[existingIndex], incomingRow);
    });

    return this.dedupeRowsByField(mergedRows, mergeByField);
  }

  private mergeMatchedRows(existingRow: any, incomingRow: any): any {
    const existing = existingRow && typeof existingRow === 'object' ? existingRow : {};
    const incoming = incomingRow && typeof incomingRow === 'object' ? incomingRow : {};
    const keys = new Set<string>([
      ...Object.keys(existing),
      ...Object.keys(incoming)
    ]);
    const preferExisting = this.shouldPreferExistingOnMerge();

    const mergedRow: Record<string, any> = {};
    keys.forEach((key) => {
      const existingValue = existing[key];
      const incomingValue = incoming[key];

      if (preferExisting) {
        mergedRow[key] = this.isValueEmpty(existingValue) ? incomingValue : existingValue;
        return;
      }

      mergedRow[key] = this.isValueEmpty(incomingValue) ? existingValue : incomingValue;
    });

    return mergedRow;
  }

  private isSameRowByField(rowA: any, rowB: any, field: string): boolean {
    const valueA = this.normalizeMergeValue(rowA?.[field]);
    const valueB = this.normalizeMergeValue(rowB?.[field]);
    if (!valueA || !valueB) {
      return false;
    }
    return valueA === valueB;
  }

  private normalizeMergeValue(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }
    return String(value).trim().toLowerCase();
  }

  private isValueEmpty(value: any): boolean {
    if (value === undefined || value === null) {
      return true;
    }

    if (typeof value === 'string') {
      return value.trim().length === 0;
    }

    return false;
  }

  private dedupeRowsByField(rows: any[], field: string): any[] {
    const seen = new Set<string>();
    const deduped: any[] = [];

    rows.forEach((row) => {
      const key = this.normalizeMergeValue(row?.[field]);
      if (!key) {
        deduped.push(row);
        return;
      }

      if (seen.has(key)) {
        return;
      }

      seen.add(key);
      deduped.push(row);
    });

    return deduped;
  }

  private dedupeRowsBySignature(rows: any[]): any[] {
    const configuredColumns = this.getConfiguredColumnKeys();
    const seen = new Set<string>();
    const deduped: any[] = [];

    rows.forEach((row) => {
      const source = row && typeof row === 'object' ? row : {};
      const signatureColumns = configuredColumns.length > 0 ? configuredColumns : Object.keys(source);
      const signatureObject = signatureColumns.reduce((acc: Record<string, any>, columnKey: string) => {
        acc[columnKey] = source[columnKey] ?? '';
        return acc;
      }, {});
      const signature = JSON.stringify(signatureObject);

      if (seen.has(signature)) {
        return;
      }

      seen.add(signature);
      deduped.push({ ...source });
    });

    return deduped;
  }

  private extractResponseRows(response: any, action: any): any[] {
    if (!response || typeof response !== 'object') {
      return [];
    }

    const customPath = String(action?.responseDataPath || '').trim();
    if (customPath) {
      const byPath = this.getValueByPath(response, customPath);
      return this.normalizeResponseRows(byPath);
    }

    const responseList = response?.responseList;
    const entityKey = String(action?.responseEntityKey || action?.entity || '').trim();
    const rowsFromResponseList = this.extractRowsFromResponseList(responseList, entityKey);
    if (rowsFromResponseList.length) {
      return rowsFromResponseList;
    }

    if (response?.data !== undefined) {
      return this.normalizeResponseRows(response.data);
    }

    return this.normalizeResponseRows(response);
  }

  private extractRowsFromResponseList(responseList: any, entityKey: string): any[] {
    if (!responseList) {
      return [];
    }

    const entries: any[] = Array.isArray(responseList)
      ? responseList
      : (typeof responseList === 'object' ? [responseList] : []);

    const extractFromEntry = (entry: any): any[] => {
      if (!entry || typeof entry !== 'object') {
        return [];
      }

      if (entityKey && entry[entityKey] !== undefined) {
        return this.normalizeResponseRows(entry[entityKey]);
      }

      for (const key of Object.keys(entry)) {
        const rows = this.normalizeResponseRows(entry[key]);
        if (rows.length) {
          return rows;
        }
      }

      return [];
    };

    for (const entry of entries) {
      const rows = extractFromEntry(entry);
      if (rows.length) {
        return rows;
      }
    }

    return [];
  }

  private normalizeResponseRows(value: any): any[] {
    const values = Array.isArray(value) ? value : [value];

    return values
      .map((item) => {
        if (!item || typeof item !== 'object') {
          return null;
        }

        if (item.data && typeof item.data === 'object') {
          return item.data;
        }

        return item;
      })
      .filter((item): item is Record<string, any> => !!item);
  }

  private findMatchingResponseRowForTarget(
    action: any,
    targetRow: any,
    responseRows: any[]
  ): any | null {
    if (!targetRow || !responseRows.length) {
      return null;
    }

    const matchField = String(action?.matchByField || 'item_id').trim();
    if (!matchField) {
      return null;
    }

    const targetValue = targetRow?.[matchField];
    if (targetValue === undefined || targetValue === null || targetValue === '') {
      return null;
    }

    const normalizedTarget = String(targetValue).trim().toLowerCase();
    const matched = responseRows.find((row) => {
      const rowValue = row?.[matchField];
      if (rowValue === undefined || rowValue === null || rowValue === '') {
        return false;
      }
      return String(rowValue).trim().toLowerCase() === normalizedTarget;
    });

    return matched || null;
  }

  private mergeResponseRowIntoTableRow(targetRow: any, responseRow: Record<string, any>): void {
    if (!targetRow || !responseRow || typeof responseRow !== 'object') {
      return;
    }

    const uiColumns = Array.isArray(this.data?.headerColumns)
      ? this.data.headerColumns.map((column: any) => column?.key).filter((key: any) => !!key)
      : this.displayedColumns;
    const uiColumnSet = new Set<string>(uiColumns);

    Object.entries(responseRow).forEach(([field, value]) => {
      if (!uiColumnSet.has(field)) {
        return;
      }

      targetRow[field] = this.normalizeResponseValueForColumn(field, value);
    });
  }

  private normalizeResponseValueForColumn(column: string, value: any): any {
    if (!this.isDropdownColumn(column)) {
      return value;
    }

    const options = this.getDropdownOptions(column);
    if (!options.length) {
      return value;
    }

    const stringValue = String(value ?? '').trim();
    const directMatch = options.find((option) =>
      String(option).trim().toLowerCase() === stringValue.toLowerCase()
    );

    if (directMatch !== undefined) {
      return directMatch;
    }

    const numericValue = this.toNumber(value);
    if (numericValue !== null) {
      const numericMatch = options.find((option) => {
        const optionNumber = this.toNumber(option);
        return optionNumber !== null && optionNumber === numericValue;
      });

      if (numericMatch !== undefined) {
        return numericMatch;
      }
    }

    return value;
  }

  private toNumber(value: any): number | null {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const cleaned = value.replace('%', '').trim();
      if (!cleaned) {
        return null;
      }
      const numeric = Number(cleaned);
      return Number.isNaN(numeric) ? null : numeric;
    }

    return null;
  }

  private isLikelyNumericColumn(column: string): boolean {
    const normalizedKey = String(column || '').trim().toLowerCase();
    const normalizedLabel = String(this.columnLabels?.[column] || '')
      .trim()
      .toLowerCase();

    if (!normalizedKey && !normalizedLabel) {
      return false;
    }

    if (this.nonNumericColumnHints.some((hint) => normalizedKey.includes(hint) || normalizedLabel.includes(hint))) {
      return false;
    }

    if (normalizedLabel.includes('%') || normalizedKey.includes('%')) {
      return true;
    }

    return this.numericColumnHints.some((hint) => normalizedKey.includes(hint) || normalizedLabel.includes(hint));
  }

  private isNumericValue(value: any): boolean {
    return this.toNumber(value) !== null;
  }

  private getValueByPath(source: any, path: string): any {
    return path
      .split('.')
      .map((segment) => segment.trim())
      .filter((segment) => segment.length > 0)
      .reduce((acc, segment) => {
        if (acc === undefined || acc === null) {
          return undefined;
        }

        const arrayMatch = segment.match(/^([^[\]]+)\[(\d+)\]$/);
        if (arrayMatch) {
          const key = arrayMatch[1];
          const index = Number(arrayMatch[2]);
          const value = acc?.[key];
          return Array.isArray(value) ? value[index] : undefined;
        }

        return acc?.[segment];
      }, source);
  }

  private buildRequestData(action: any, row: any): Record<string, any> {
    const configuredFields = Array.isArray(action?.requestDataFields)
      ? action.requestDataFields
      : this.displayedColumns;

    const fields: string[] = configuredFields
      .map((field: any) => String(field || '').trim())
      .filter((field: string) => field.length > 0);

    const requestData: Record<string, any> = {};
    fields.forEach((field: string) => {
      requestData[field] = this.normalizeActionFieldValue(field, row?.[field]);
    });

    return requestData;
  }

  private normalizeActionFieldValue(field: string, value: any): any {
    if (value === undefined || value === null) {
      return value;
    }

    if (field === this.discountTypeKey && typeof value === 'string') {
      return value.trim().toLowerCase();
    }

    if (field === this.lineDiscountKey) {
      if (typeof value === 'number') {
        return value;
      }

      if (typeof value === 'string') {
        const cleaned = value.replace('%', '').trim();
        const numeric = Number(cleaned);
        return Number.isNaN(numeric) ? value : numeric;
      }
    }

    return value;
  }

  private deepClone<T>(value: T): T {
    if (value === undefined || value === null) {
      return value;
    }
    return JSON.parse(JSON.stringify(value));
  }

  private publishWidgetData(): void {
    const widgetId = this.data?.widgetId || this.widgetId;
    if (!widgetId) {
      return;
    }

    const rows = Array.isArray(this.dataSource)
      ? this.dataSource.map((row: any) => ({ ...row }))
      : [];
    this.globalFilterService.setWidgetData(widgetId, rows);
  }

  private restoreFromWidgetData(): boolean {
    const widgetRows = this.globalFilterService.getWidgetData(this.widgetId);
    if (!widgetRows.length) {
      return false;
    }

    if (!this.hasCompatibleWidgetRows(widgetRows)) {
      this.globalFilterService.clearWidgetData(this.widgetId);
      return false;
    }

    const restoredRows = widgetRows.map((row: any) => ({ ...row }));
    this.dataSource = this.normalizeRowsToConfiguredColumns(restoredRows);
    this.originalDataSource = [...this.dataSource];
    this.initializeDropdownDefaults(this.dataSource);
    this.refreshDisplayedColumnsFromData();
    this.publishWidgetData();
    if (!!this.data?.isPagination) {
      this.setPagedData();
    }
    this.isLoading = false;
    return true;
  }

  private hasCompatibleWidgetRows(rows: any[]): boolean {
    const configuredColumns = this.getConfiguredColumnKeys();
    if (!configuredColumns.length) {
      return true;
    }

    const nonDropdownColumns = configuredColumns.filter((column) => !this.isDropdownColumn(column));
    const columnsToCheck = nonDropdownColumns.length > 0 ? nonDropdownColumns : configuredColumns;

    return rows.some((row) =>
      columnsToCheck.some((column) => !this.isValueEmpty(row?.[column]))
    );
  }

  private resetTableState(): void {
    this.dataSource = [];
    this.originalDataSource = [];
    this.displayedColumns = this.data?.headerColumns?.map((col: any) => col.key) || this.displayedColumns;
    this.publishWidgetData();
    if (!!this.data?.isPagination) {
      this.setPagedData();
    }
  }

  private initializeDropdownDefaults(rows: any[]): void {
    rows.forEach((row) => {
      this.displayedColumns.forEach((column) => {
        if (!this.isDropdownColumn(column)) {
          return;
        }

        if (!this.shouldDefaultToFirstOption(column)) {
          return;
        }

        const options = this.getDropdownOptions(column);
        if (!options.length) {
          return;
        }

        const value = row[column];
        if (value === undefined || value === null || value === '') {
          row[column] = options[0];
        }
      });
    });
  }

  private shouldDefaultToFirstOption(column: string): boolean {
    const colConfig = this.columnConfigMap[column];
    if (typeof colConfig?.defaultToFirstOption === 'boolean') {
      return colConfig.defaultToFirstOption;
    }
    return true;
  }

  private setPagedData() {
    const startIndex = (this.pageIndex || 0) * (this.pageSize || 10);
    const endIndex = startIndex + (this.pageSize || 10);
    this.pagedData = this.dataSource.slice(startIndex, endIndex);
  }


  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }
}
